/**
 * Security Logging System
 * Implements comprehensive audit trail and security event logging
 * Complies with security requirements for audit trails and SIEM integration
 */

import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

// Log levels for different types of events
export const LOG_LEVELS = {
  CRITICAL: 'CRITICAL',    // Security breaches, system failures
  HIGH: 'HIGH',            // Failed logins, permission denials, admin actions
  MEDIUM: 'MEDIUM',        // User actions, data changes
  LOW: 'LOW',              // General application events
  DEBUG: 'DEBUG'           // Development and debugging
};

// Event categories for better organization
export const EVENT_CATEGORIES = {
  AUTHENTICATION: 'AUTHENTICATION',
  AUTHORIZATION: 'AUTHORIZATION',
  DATA_ACCESS: 'DATA_ACCESS',
  DATA_MODIFICATION: 'DATA_MODIFICATION',
  FINANCIAL: 'FINANCIAL',
  ADMIN_ACTION: 'ADMIN_ACTION',
  SECURITY: 'SECURITY',
  SYSTEM: 'SYSTEM',
  USER_ACTION: 'USER_ACTION'
};

// Event types for specific actions
export const EVENT_TYPES = {
  // Authentication events
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILED: 'LOGIN_FAILED',
  LOGOUT: 'LOGOUT',
  PASSWORD_CHANGE: 'PASSWORD_CHANGE',
  PASSWORD_RESET_REQUEST: 'PASSWORD_RESET_REQUEST',
  PASSWORD_RESET_SUCCESS: 'PASSWORD_RESET_SUCCESS',
  TOTP_ENABLED: 'TOTP_ENABLED',
  TOTP_DISABLED: 'TOTP_DISABLED',
  TOTP_VERIFICATION_SUCCESS: 'TOTP_VERIFICATION_SUCCESS',
  TOTP_VERIFICATION_FAILED: 'TOTP_VERIFICATION_FAILED',
  
  // Authorization events
  PERMISSION_DENIED: 'PERMISSION_DENIED',
  UNAUTHORIZED_ACCESS: 'UNAUTHORIZED_ACCESS',
  ROLE_CHANGE: 'ROLE_CHANGE',
  
  // Admin actions
  USER_CREATED: 'USER_CREATED',
  USER_DELETED: 'USER_DELETED',
  USER_UPDATED: 'USER_UPDATED',
  ADMIN_PASSWORD_RESET: 'ADMIN_PASSWORD_RESET',
  ADMIN_ACTION: 'ADMIN_ACTION',
  
  // Financial events
  DP_PURCHASE_INITIATED: 'DP_PURCHASE_INITIATED',
  DP_PURCHASE_SUCCESS: 'DP_PURCHASE_SUCCESS',
  DP_PURCHASE_FAILED: 'DP_PURCHASE_FAILED',
  DP_TRANSFER: 'DP_TRANSFER',
  PAYMENT_PROCESSED: 'PAYMENT_PROCESSED',
  PAYMENT_FAILED: 'PAYMENT_FAILED',
  
  // User actions
  SHOP_PURCHASE: 'SHOP_PURCHASE',
  MVOLA_PURCHASE: 'MVOLA_PURCHASE',
  
  // Marketplace events
  MARKETPLACE_LISTING_CREATED: 'MARKETPLACE_LISTING_CREATED',
  MARKETPLACE_LISTING_UPDATED: 'MARKETPLACE_LISTING_UPDATED',
  MARKETPLACE_LISTING_DELETED: 'MARKETPLACE_LISTING_DELETED',
  MARKETPLACE_PURCHASE_INITIATED: 'MARKETPLACE_PURCHASE_INITIATED',
  MARKETPLACE_PURCHASE_COMPLETED: 'MARKETPLACE_PURCHASE_COMPLETED',
  MARKETPLACE_PURCHASE_FAILED: 'MARKETPLACE_PURCHASE_FAILED',
  MARKETPLACE_TRANSACTION_COMPLETED: 'MARKETPLACE_TRANSACTION_COMPLETED',
  MARKETPLACE_DISPUTE_CREATED: 'MARKETPLACE_DISPUTE_CREATED',
  MARKETPLACE_DISPUTE_RESOLVED: 'MARKETPLACE_DISPUTE_RESOLVED',
  MARKETPLACE_REVIEW_ADDED: 'MARKETPLACE_REVIEW_ADDED',
  MARKETPLACE_ESCROW_CREATED: 'MARKETPLACE_ESCROW_CREATED',
  MARKETPLACE_ESCROW_RELEASED: 'MARKETPLACE_ESCROW_RELEASED',
  
  // Session management
  SESSION_STARTED: 'SESSION_STARTED',
  SESSION_REFRESHED: 'SESSION_REFRESHED',
  SESSION_WARNING: 'SESSION_WARNING',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  
  // Rate limiting
  RATE_LIMIT_ATTEMPT: 'RATE_LIMIT_ATTEMPT',
  RATE_LIMIT_HIT: 'RATE_LIMIT_HIT',
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',
  BRUTE_FORCE_ATTEMPT: 'BRUTE_FORCE_ATTEMPT',
  
  // Input validation
  INPUT_VALIDATION_FAILED: 'INPUT_VALIDATION_FAILED',
  INPUT_SANITIZATION: 'INPUT_SANITIZATION',
  INJECTION_ATTEMPT: 'INJECTION_ATTEMPT',
  
  // Data events
  DATA_CREATED: 'DATA_CREATED',
  DATA_UPDATED: 'DATA_UPDATED',
  DATA_DELETED: 'DATA_DELETED',
  DATA_EXPORTED: 'DATA_EXPORTED',
  
  // Security events
  SUSPICIOUS_ACTIVITY: 'SUSPICIOUS_ACTIVITY',
  BRUTE_FORCE_ATTEMPT: 'BRUTE_FORCE_ATTEMPT',
  ACCOUNT_LOCKED: 'ACCOUNT_LOCKED',
  SECURITY_ALERT: 'SECURITY_ALERT',
  
  // System events
  SYSTEM_ERROR: 'SYSTEM_ERROR',
  API_CALL: 'API_CALL',
  CONFIGURATION_CHANGE: 'CONFIGURATION_CHANGE',
  
  // Backup events
  DATA_BACKUP_CREATED: 'DATA_BACKUP_CREATED',
  DATA_BACKUP_FAILED: 'DATA_BACKUP_FAILED',
  DATA_RESTORED: 'DATA_RESTORED',
  DATA_RESTORE_FAILED: 'DATA_RESTORE_FAILED',
  DATA_BACKUP_CLEANUP: 'DATA_BACKUP_CLEANUP'
};

/**
 * Centralized logging function
 * @param {string} level - Log level (CRITICAL, HIGH, MEDIUM, LOW, DEBUG)
 * @param {string} category - Event category
 * @param {string} eventType - Specific event type
 * @param {string} message - Human-readable message
 * @param {Object} details - Additional event details
 * @param {string} userId - User ID (if applicable)
 * @param {string} sessionId - Session ID (if applicable)
 * @param {Object} metadata - Additional metadata
 */
export async function logEvent({
  level = LOG_LEVELS.MEDIUM,
  category = EVENT_CATEGORIES.SYSTEM,
  eventType,
  message,
  details = {},
  userId = null,
  sessionId = null,
  metadata = {}
}) {
  try {
    // Create structured log entry
    const logEntry = {
      timestamp: serverTimestamp(),
      level,
      category,
      eventType,
      message,
      details,
      userId,
      sessionId,
      metadata: {
        ...metadata,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Server',
        ipAddress: await getClientIP(),
        appVersion: process.env.REACT_APP_VERSION || '1.0.0',
        environment: process.env.NODE_ENV || 'development',
        browserInfo: getBrowserInfo(),
        screenResolution: getScreenResolution(),
        language: typeof navigator !== 'undefined' ? navigator.language : 'unknown',
        platform: typeof navigator !== 'undefined' ? navigator.platform : 'unknown',
        timestamp: new Date().toISOString()
      },
      // Add hash for integrity verification
      integrityHash: generateIntegrityHash(level, category, eventType, message, details)
    };

    // Store in Firestore
    await addDoc(collection(db, 'security_logs'), logEntry);

    // Also log to console for development
    if (process.env.NODE_ENV === 'development') {
      console.log(`[${level}] ${category}:${eventType} - ${message}`, details);
    }

    return { success: true, logId: logEntry.timestamp };

  } catch (error) {
    console.error('Failed to log security event:', error);
    // Fallback to console logging
    console.error(`[${level}] ${category}:${eventType} - ${message}`, details);
    return { success: false, error: error.message };
  }
}

/**
 * Log authentication events
 */
export async function logAuthEvent(eventType, userId, details = {}) {
  const messages = {
    [EVENT_TYPES.LOGIN_SUCCESS]: `User ${userId} logged in successfully`,
    [EVENT_TYPES.LOGIN_FAILED]: `Failed login attempt for user ${userId}`,
    [EVENT_TYPES.LOGOUT]: `User ${userId} logged out`,
    [EVENT_TYPES.PASSWORD_CHANGE]: `User ${userId} changed password`,
    [EVENT_TYPES.PASSWORD_RESET_REQUEST]: `Password reset requested for user ${userId}`,
    [EVENT_TYPES.PASSWORD_RESET_SUCCESS]: `Password reset completed for user ${userId}`,
    [EVENT_TYPES.TOTP_ENABLED]: `TOTP enabled for user ${userId}`,
    [EVENT_TYPES.TOTP_DISABLED]: `TOTP disabled for user ${userId}`,
    [EVENT_TYPES.TOTP_VERIFICATION_SUCCESS]: `TOTP verification successful for user ${userId}`,
    [EVENT_TYPES.TOTP_VERIFICATION_FAILED]: `TOTP verification failed for user ${userId}`
  };

  return await logEvent({
    level: LOG_LEVELS.HIGH,
    category: EVENT_CATEGORIES.AUTHENTICATION,
    eventType,
    message: messages[eventType] || `Authentication event: ${eventType}`,
    details,
    userId
  });
}

/**
 * Log admin actions
 */
export async function logAdminAction(eventType, adminUserId, targetUserId, details = {}) {
  const messages = {
    [EVENT_TYPES.USER_CREATED]: `Admin ${adminUserId} created user ${targetUserId}`,
    [EVENT_TYPES.USER_DELETED]: `Admin ${adminUserId} deleted user ${targetUserId}`,
    [EVENT_TYPES.USER_UPDATED]: `Admin ${adminUserId} updated user ${targetUserId}`,
    [EVENT_TYPES.ADMIN_PASSWORD_RESET]: `Admin ${adminUserId} reset password for user ${targetUserId}`,
    [EVENT_TYPES.ADMIN_ACTION]: `Admin ${adminUserId} performed action: ${eventType}`
  };

  return await logEvent({
    level: LOG_LEVELS.HIGH,
    category: EVENT_CATEGORIES.ADMIN_ACTION,
    eventType,
    message: messages[eventType] || `Admin action: ${eventType}`,
    details: { ...details, targetUserId },
    userId: adminUserId
  });
}

/**
 * Log financial transactions
 */
export async function logFinancialEvent(eventType, userId, amount, details = {}) {
  const messages = {
    [EVENT_TYPES.DP_PURCHASE_INITIATED]: `User ${userId} initiated DP purchase: ${amount} DP`,
    [EVENT_TYPES.DP_PURCHASE_SUCCESS]: `User ${userId} successfully purchased ${amount} DP`,
    [EVENT_TYPES.DP_PURCHASE_FAILED]: `User ${userId} failed to purchase ${amount} DP`,
    [EVENT_TYPES.DP_TRANSFER]: `User ${userId} transferred ${amount} DP`,
    [EVENT_TYPES.PAYMENT_PROCESSED]: `Payment processed for user ${userId}: ${amount}`,
    [EVENT_TYPES.PAYMENT_FAILED]: `Payment failed for user ${userId}: ${amount}`
  };

  return await logEvent({
    level: LOG_LEVELS.HIGH,
    category: EVENT_CATEGORIES.FINANCIAL,
    eventType,
    message: messages[eventType] || `Financial event: ${eventType}`,
    details: { ...details, amount },
    userId
  });
}

/**
 * Log security events
 */
export async function logSecurityEvent(eventType, userId, details = {}) {
  const messages = {
    [EVENT_TYPES.SUSPICIOUS_ACTIVITY]: `Suspicious activity detected for user ${userId}`,
    [EVENT_TYPES.BRUTE_FORCE_ATTEMPT]: `Brute force attempt detected for user ${userId}`,
    [EVENT_TYPES.ACCOUNT_LOCKED]: `Account locked for user ${userId}`,
    [EVENT_TYPES.SECURITY_ALERT]: `Security alert: ${eventType}`,
    [EVENT_TYPES.PERMISSION_DENIED]: `Permission denied for user ${userId}`,
    [EVENT_TYPES.UNAUTHORIZED_ACCESS]: `Unauthorized access attempt by user ${userId}`
  };

  return await logEvent({
    level: LOG_LEVELS.CRITICAL,
    category: EVENT_CATEGORIES.SECURITY,
    eventType,
    message: messages[eventType] || `Security event: ${eventType}`,
    details,
    userId
  });
}

/**
 * Log user actions
 */
export async function logUserAction(eventType, userId, details = {}) {
  return await logEvent({
    level: LOG_LEVELS.MEDIUM,
    category: EVENT_CATEGORIES.USER_ACTION,
    eventType,
    message: `User action: ${eventType}`,
    details,
    userId
  });
}

/**
 * Get client IP address (simplified for web app)
 */
async function getClientIP() {
  try {
    // Try to get IP from a public service
    const response = await fetch('https://api.ipify.org?format=json');
    if (response.ok) {
      const data = await response.json();
      return data.ip || 'unknown';
    }
    
    // Fallback: try another service
    const response2 = await fetch('https://ipapi.co/json/');
    if (response2.ok) {
      const data = await response2.json();
      return data.ip || 'unknown';
    }
    
    // Final fallback
    return 'web-client';
  } catch (error) {
    console.warn('Could not fetch IP address:', error);
    return 'web-client';
  }
}

/**
 * Get detailed browser information
 */
function getBrowserInfo() {
  if (typeof navigator === 'undefined') return {};
  
  const userAgent = navigator.userAgent;
  return {
    browser: getBrowserName(userAgent),
    version: getBrowserVersion(userAgent),
    os: getOperatingSystem(userAgent),
    isMobile: /Mobile|Android|iPhone|iPad/.test(userAgent),
    isTablet: /iPad|Android(?!.*Mobile)/.test(userAgent)
  };
}

/**
 * Get screen resolution
 */
function getScreenResolution() {
  if (typeof window === 'undefined' || !window.screen) return 'unknown';
  
  return {
    width: window.screen.width,
    height: window.screen.height,
    colorDepth: window.screen.colorDepth,
    pixelDepth: window.screen.pixelDepth
  };
}

/**
 * Extract browser name from user agent
 */
function getBrowserName(userAgent) {
  if (userAgent.includes('Chrome') && !userAgent.includes('Edge')) return 'Chrome';
  if (userAgent.includes('Firefox')) return 'Firefox';
  if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) return 'Safari';
  if (userAgent.includes('Edge')) return 'Edge';
  if (userAgent.includes('Opera')) return 'Opera';
  return 'Unknown';
}

/**
 * Extract browser version from user agent
 */
function getBrowserVersion(userAgent) {
  const browsers = [
    { name: 'Chrome', regex: /Chrome\/(\d+\.\d+)/ },
    { name: 'Firefox', regex: /Firefox\/(\d+\.\d+)/ },
    { name: 'Safari', regex: /Version\/(\d+\.\d+)/ },
    { name: 'Edge', regex: /Edge\/(\d+\.\d+)/ },
    { name: 'Opera', regex: /Opera\/(\d+\.\d+)/ }
  ];
  
  for (const browser of browsers) {
    const match = userAgent.match(browser.regex);
    if (match) return match[1];
  }
  
  return 'Unknown';
}

/**
 * Extract operating system from user agent
 */
function getOperatingSystem(userAgent) {
  if (userAgent.includes('Windows')) return 'Windows';
  if (userAgent.includes('Mac')) return 'macOS';
  if (userAgent.includes('Linux')) return 'Linux';
  if (userAgent.includes('Android')) return 'Android';
  if (userAgent.includes('iPhone') || userAgent.includes('iPad')) return 'iOS';
  return 'Unknown';
}

/**
 * Generate integrity hash for log verification
 */
function generateIntegrityHash(level, category, eventType, message, details) {
  const data = `${level}:${category}:${eventType}:${message}:${JSON.stringify(details)}`;
  // Simple hash function (in production, use crypto.subtle.digest)
  let hash = 0;
  for (let i = 0; i < data.length; i++) {
    const char = data.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return hash.toString(16);
}

/**
 * Batch log multiple events (for performance)
 */
export async function logBatchEvents(events) {
  const promises = events.map(event => logEvent(event));
  return await Promise.allSettled(promises);
}

/**
 * Get logs for a specific user (admin only)
 */
export async function getUserLogs(userId, limit = 100) {
  // This would be implemented with proper Firestore queries
  // and admin-only access controls
  console.log(`Getting logs for user: ${userId}`);
  return [];
}

/**
 * Get login history for a specific user
 */
export async function getLoginHistory(userId, limit = 50) {
  try {
    const { collection, query, where, orderBy, limitToLast, getDocs } = await import('firebase/firestore');
    
    // Query security logs for login events for this user
    const loginEventsQuery = query(
      collection(db, 'security_logs'),
      where('userId', '==', userId),
      where('eventType', 'in', [EVENT_TYPES.LOGIN_SUCCESS, EVENT_TYPES.LOGIN_FAILED, EVENT_TYPES.LOGOUT]),
      orderBy('timestamp', 'desc'),
      limitToLast(limit)
    );
    
    const snapshot = await getDocs(loginEventsQuery);
    const loginHistory = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      // Convert Firestore timestamp to JavaScript Date
      timestamp: doc.data().timestamp?.toDate?.() || new Date()
    }));
    
    return { success: true, data: loginHistory };
  } catch (error) {
    console.error('Error fetching login history:', error);
    return { success: false, error: error.message, data: [] };
  }
}

/**
 * Get security logs by level (admin only)
 */
export async function getSecurityLogs(level, limit = 100) {
  // This would be implemented with proper Firestore queries
  // and admin-only access controls
  console.log(`Getting security logs for level: ${level}`);
  return [];
}

/**
 * Debug function to create test login events (development only)
 */
export async function createTestLoginEvents(userId) {
  if (process.env.NODE_ENV !== 'development') {
    console.warn('createTestLoginEvents is only available in development mode');
    return;
  }
  
  try {
    // Create a few test login events
    const testEvents = [
      {
        eventType: EVENT_TYPES.LOGIN_SUCCESS,
        message: `Test login success for user ${userId}`,
        details: { testEvent: true, loginMethod: 'email_password' }
      },
      {
        eventType: EVENT_TYPES.LOGIN_FAILED,
        message: `Test login failed for user ${userId}`,
        details: { testEvent: true, errorCode: 'auth/wrong-password' }
      },
      {
        eventType: EVENT_TYPES.LOGOUT,
        message: `Test logout for user ${userId}`,
        details: { testEvent: true }
      }
    ];
    
    for (const event of testEvents) {
      await logEvent({
        level: LOG_LEVELS.HIGH,
        category: EVENT_CATEGORIES.AUTHENTICATION,
        eventType: event.eventType,
        message: event.message,
        details: event.details,
        userId
      });
    }
    
    console.log('Test login events created successfully');
    return { success: true };
  } catch (error) {
    console.error('Error creating test login events:', error);
    return { success: false, error: error.message };
  }
}
